export type MemberPosition = "CLIENT" | "REVIEWER" | "ADMIN" | "PRODUCER" | "SPECIALIST" | "DIRECTOR";

export const isClientGroupPosition = (position: string): boolean =>
  position === "CLIENT" || position === "REVIEWER";

export const isProducerGroupPosition = (position: string): boolean =>
  position === "PRODUCER" || position === "DIRECTOR" || position === "ADMIN";

/**
 * 責任者プロデューサー（PRODUCER）と管理者（ADMIN）のみ。タスクボードの共有設定
 * 変更など、ボードオーナー格の操作可否に使う（ディレクターは含まない）。
 */
export const isResponsibleProducerPosition = (position: string): boolean =>
  position === "PRODUCER" || position === "ADMIN";

export const isSpecialistPosition = (position: string): boolean =>
  position === "SPECIALIST";

export const isPrimaryClientPosition = (position: string): boolean =>
  position === "CLIENT";

export const isPrimaryPosition = (position: string): boolean =>
  position === "CLIENT" || position === "PRODUCER";

export const canControlMembers = (position: string): boolean =>
  position === "CLIENT" || position === "PRODUCER";

// ===== スタッフクレジットの権限判定 =====

/**
 * クレジット書き込み可。BE credits API の CREDIT_WRITER_POSITIONS
 * （master_admin=ADMIN / owner=CLIENT / producer=PRODUCER）と一致させる。
 */
export const canEditCredit = (position: string): boolean =>
  position === "PRODUCER" || position === "ADMIN" || position === "CLIENT";

/** 編集権限を持たないメンバーは閲覧のみ */
export const canViewCreditOnly = (position: string): boolean =>
  position === "DIRECTOR" || position === "REVIEWER" || position === "SPECIALIST";

// ===== チャットバブルの左右判定（プレビュー / トークルーム共通） =====

/**
 * position の陣営ラベル。チャットバブルの左右判定と、その変化検知ログに使う。
 */
export const positionGroupOf = (
  position: string | null | undefined,
): "client" | "producer" | "other" => {
  if (!position) return "other";
  if (isClientGroupPosition(position)) return "client";
  if (isProducerGroupPosition(position)) return "producer";
  return "other";
};

/**
 * チャットバブルを右側（自分側）へ表示するかの判定
 * （プレビューのショットコメント / トークルームで共通）。
 *
 * 優先順位:
 * 1. 投稿者が自分自身（authorId === currentUserId）なら常に右。
 *    membership の取得遅延・position 欠落があっても自分の投稿が
 *    左（相手側）に落ちないための最優先ルール。
 * 2. 自分と投稿者の position が同じ陣営なら右
 *    （クライアント系 = CLIENT/REVIEWER、プロデューサー系 =
 *    PRODUCER/DIRECTOR/ADMIN）。
 * 3. どちらでも判定できない（position 未取得・SPECIALIST 等）場合は左。
 */
export const isMessageOnRightSide = (args: {
  authorId: string | number | null | undefined;
  authorPosition: string | null | undefined;
  currentUserId: string | number | null | undefined;
  viewerPosition: string | null | undefined;
}): boolean => {
  const { authorId, authorPosition, currentUserId, viewerPosition } = args;
  if (
    authorId != null &&
    currentUserId != null &&
    String(authorId) === String(currentUserId)
  ) {
    return true;
  }
  const viewerGroup = positionGroupOf(viewerPosition);
  if (viewerGroup === "other") return false;
  return positionGroupOf(authorPosition) === viewerGroup;
};

/**
 * PRODUCER / DIRECTOR のみ SPECIALIST メンバーを閲覧できる
 */
export const shouldShowSpecialists = (currentUserPosition: string): boolean =>
  currentUserPosition === "PRODUCER" || currentUserPosition === "DIRECTOR";

/**
 * プロジェクトメンバー一覧で Bookmark（フォロー）操作の対象になるかを判定する。
 *
 * 判定軸:
 * - 招待中ではない
 * - 自分自身ではない
 * - `user_id` が解決済み
 * - クライアントグループ（CLIENT / REVIEWER）でも ADMIN でもない
 *
 * 「フォロー = オファー候補」という現状の運用に合わせ、現時点でオファー対象に
 * ならないポジションは Bookmark UI 自体を非表示にする。FE 側のフィルタのみで、
 * DB 上のフォロー関係には影響しない。
 */
export const isFollowableMember = (
  membership: { user_id?: number; position?: string; is_invited?: boolean },
  currentUserId: number | undefined,
): boolean => {
  const { user_id, position, is_invited } = membership;
  if (!user_id) return false;
  if (is_invited) return false;
  if (currentUserId !== undefined && user_id === currentUserId) return false;
  const pos = position ?? "";
  if (isClientGroupPosition(pos)) return false;
  if (pos === "ADMIN") return false;
  return true;
};

/**
 * オファー作成ボタンを表示してよいかどうかの判定。
 *
 * - PRODUCER グループ（PRODUCER / DIRECTOR / ADMIN）: 常に可
 * - SPECIALIST: 「自分が contractor として引き受けたオファーの中に、
 *   再委託を認めるフラグ (`allow_subcontracting=true`) が 1 件以上ある場合」のみ可。
 *   FE 側では `useCanCreateOffer` フックが該当スレッドを購読し、
 *   この関数には集計済みの `hasReceivedAllowedSubcontract` フラグを渡す。
 * - CLIENT グループ（CLIENT / REVIEWER）: プロジェクトが
 *   `is_direct_subcontract_allowed` を有効にしている場合のみ可
 */
export interface CanCreateOfferContext {
  /** SPECIALIST 用: 自分が contractor かつ allow_subcontracting=true のオファーが1件以上 */
  hasReceivedAllowedSubcontract?: boolean;
  /** プロジェクトの「直接委託可」フラグ（CLIENT グループ用） */
  isDirectSubcontractAllowed?: boolean;
}

export const canCreateOffer = (
  position: string | undefined,
  ctx: CanCreateOfferContext = {},
): boolean => {
  const pos = (position ?? "").toUpperCase();
  if (isProducerGroupPosition(pos)) return true;
  if (isSpecialistPosition(pos)) return !!ctx.hasReceivedAllowedSubcontract;
  if (isClientGroupPosition(pos)) return !!ctx.isDirectSubcontractAllowed;
  return false;
};

/**
 * メンバー配列を main（CLIENT/REVIEWER/PRODUCER/DIRECTOR/ADMIN）と
 * specialist（SPECIALIST）に分離する
 */
export const splitMembersBySpecialist = <T extends { position: string }>(
  members: T[],
): { mainMembers: T[]; specialistMembers: T[] } => {
  const mainMembers: T[] = [];
  const specialistMembers: T[] = [];
  for (const m of members) {
    if (isSpecialistPosition(m.position)) {
      specialistMembers.push(m);
    } else {
      mainMembers.push(m);
    }
  }
  return { mainMembers, specialistMembers };
};

// ===== グループ内の表示順（プロジェクトバナーのユーザーリストと同じ並び） =====

/** クライアントグループの表示順: 責任者 → 一般 */
export const CLIENT_GROUP_POSITION_ORDER: string[] = ["CLIENT", "REVIEWER"];

/** プロデューサーグループの表示順: 責任者 → ディレクター → 管理者 */
export const PRODUCER_GROUP_POSITION_ORDER: string[] = [
  "PRODUCER",
  "DIRECTOR",
  "ADMIN",
];

/**
 * ポジション優先度順にメンバーを整列する。同一ポジション内は入力順
 * （= サーバーの order_user 順）を維持する。
 */
export const orderMembersByPosition = <T extends { position: string }>(
  members: T[],
  positionOrder: string[],
): T[] =>
  positionOrder.flatMap((position) =>
    members.filter((m) => m.position === position),
  );

/**
 * アバタースタック表示用にメンバー全体を 1 列へ整列する:
 * クライアントグループ → プロデューサーグループ → スペシャリスト。
 * プロジェクトバナー / 管理画面のメンバーシップセルで並びを揃えるために使う。
 */
export const orderMembersForStack = <T extends { position: string }>(
  members: T[],
): T[] => [
  ...orderMembersByPosition(
    members.filter((m) => isClientGroupPosition(m.position)),
    CLIENT_GROUP_POSITION_ORDER,
  ),
  ...orderMembersByPosition(
    members.filter((m) => isProducerGroupPosition(m.position)),
    PRODUCER_GROUP_POSITION_ORDER,
  ),
  ...members.filter((m) => isSpecialistPosition(m.position)),
];
